<template>
  <button
    v-clickoutside="handleBlur"
    class="common-button"
    @click="handleClick"
    :disabled="disabled || loading"
    :autofocus="autofocus"
    :type="nativeType"
    :class="[
      type ? 'common-button--' + type : '',
      size ? 'common-button--' + size : '',
      {
        'is-disabled': disabled,
        'is-loading': loading,
        'is-active': isActive,
        'is-plain': plain
      }
    ]"
    :style="{ ...textColor }"
  >
    <svg-icon v-if="icon" :name="icon" class="left-icon" />
    <span v-if="$slots.default"><slot></slot></span>
    <div v-if="loading" class="common-loading">
      <i class="loading-icon"></i>
    </div>
  </button>
</template>
<script>
import Clickoutside from '@/tools/clickoutside'
export default {
  name: 'commonButton',
  directives: { Clickoutside },
  data() {
    return {
      isActive: false
    }
  },
  props: {
    type: {
      type: String,
      default: 'default'
    },
    size: String,
    icon: {
      type: String,
      default: ''
    },
    nativeType: {
      type: String,
      default: 'button'
    },
    color: {
      type: String,
      default: ''
    },
    loading: Boolean,
    disabled: Boolean,
    autofocus: Boolean,
    plain: Boolean
  },
  watch: {
    disabled(value) {
      if (value) {
        this.handleBlur()
      }
    }
  },
  computed: {
    textColor() {
      if (this.type === 'text' && this.color) {
        return { color: this.color }
      }
      return {}
    }
  },
  methods: {
    handleClick(event) {
      this.$emit('click', event)
      this.isActive = true
    },
    handleBlur() {
      this.isActive = false
    }
  }
}
</script>
<style lang="scss" scoped>
@keyframes rotating {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(360deg);
  }
}
.common-button {
  position: relative;
  display: inline-block;
  line-height: 34px;
  height: 36px;
  white-space: nowrap;
  cursor: pointer;
  background-color: #fff;
  border: 1px solid #b4bac8;
  border-radius: 4px;
  color: #4c556c;
  text-align: center;
  box-sizing: border-box;
  outline: none;
  margin: 0;
  padding: 0px 10px;
  user-select: none;
  font-size: 14px;
  overflow: hidden;
  min-width: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  span {
    // vertical-align: middle;
    font-weight: 600;
  }
  &:not(.is-disabled)::after {
    content: '';
    position: absolute;
    background-color: rgba($color: #4066D4, $alpha: 0.15);
    left: 50%;
    top: 50%;
    padding: 0;
    border-radius: 50%;
    transform: translateX(-50%) translateY(-50%);
  }
  &:not(.is-disabled):active::after {
    padding: 60%;
    transition: all 0.5s;
  }
  &+.common-button {
    margin-left: 10px;
  }
  &:not(.is-loading):not(.is-disabled):hover {
    background-color: #f5f7fc;
    color: #4066d4;
    border-color: #4066d4;
  }
  &.is-disabled {
    background-color: #f7f7f8;
    color: #dadce1;
    border-color: #dadce1;
    cursor: not-allowed;
  }
  &.is-loading {
    pointer-events: none;
    .common-loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #fff;
      border-radius: 4px;
      .loading-icon {
        width: 16px;
        height: 16px;
        background-image: url('~@/assets/img/common-button/ic-loading-default.svg');
        background-repeat: no-repeat;
        background-size: 100% 100%;
        animation: rotating 0.75s linear infinite;
      }
    }
  }
  &.is-active {
    background-color: #f5f7fc;
    color: #4066d4;
    border-color: #4066d4;
  }
  .left-icon {
    font-size: 16px;
    vertical-align: middle;
    & + span {
      margin-left: 4px;
    }
  }
}
.common-button--primary {
  background-color: #4066d4;
  color: #fff;
  border-color: #4066d4;
  &:not(.is-disabled)::after {
    background-color: rgba($color: #000000, $alpha: 0.15);
  }
  &:not(.is-loading):not(.is-disabled):hover {
    background-color: #395cbf;
    color: #fff;
    border-color: #395cbf;
  }
  &.is-disabled {
    background-color: #dadce1;
    color: #fff;
    border-color: #dadce1;
  }
  &.is-loading .common-loading {
    background-color: #4066d4;
    .loading-icon {
      background-image: url('~@/assets/img/common-button/ic-loading-primary.svg');
    }
  }
  &.is-active {
    background-color: #395cbf;
    color: #fff;
    border-color: #395cbf;
  }
}
.common-button--danger {
  background-color: #CF1322;
  color: #fff;
  border-color: #CF1322;
  &:not(.is-disabled)::after {
    background-color: rgba(207, 19, 34, 0.15);
  }
  &:not(.is-loading):not(.is-disabled):hover {
    background-color: #af1322;
    border-color: #af1322;
    color: #fff;
  }
  &.is-disabled {
    background-color: #dadce1;
    color: #fff;
    border-color: #dadce1;
  }
  &.is-loading .common-loading {
    background-color: #CF1322;
    color: #fff;
    .loading-icon {
      background-image: url('~@/assets/img/common-button/ic-loading-primary.svg');
    }
  }
  &.is-active {
    background-color: #c30f1e;
    border-color: #c30f1e;
    color: #fff;
  }
}
.common-button--danger.is-plain {
  background-color: #fff;
  color: #CF1322;
  border-color: #CF1322;
  &:not(.is-disabled)::after {
    background-color: rgba(207, 19, 34, 0.15);
  }
  &:not(.is-loading):not(.is-disabled):hover {
    background-color: #FBECED;
    border-color: #CF1322;
    color: #CF1322;
  }
  &.is-disabled {
    background-color: #fff;
    color: #DADCE1;
    border-color: #dadce1;
  }
  &.is-loading .common-loading {
    background-color: #FBECED;
    color: #CF1322;
    .loading-icon {
      background-image: url('~@/assets/img/common-button/ic-loading-danger.svg');
    }
  }
  &.is-active {
    background-color: rgba(207, 19, 34, 0.15);
    border-color: #CF1322;
    color: #CF1322;
  }
}
.common-button--standard-secondary {
  color: #4066D4;
  font-weight: 700;
  font-size: 12px;
  line-height: 22px;
  border: 1px solid #4066D4;
  background: transparent;
  span {
    font-family: 'Open Sans';
  }
}
.common-button--dangerous-secondary {
  color: #CF1322;
  font-weight: 700;
  font-size: 12px;
  line-height: 22px;
  border: 1px solid #CF1322;
  background: transparent;
  span {
    font-family: 'Open Sans';
  }
}
.common-button--text {
  background-color: transparent;
  border-color: transparent;
  color: #4066d4;
  &:not(.is-disabled)::after {
    background-color: rgba($color: #000000, $alpha: 0.1);
  }
  &:not(.is-loading):not(.is-disabled):hover {
    background-color: #e9eaed;
    color: #4066d4;
    border-color: transparent;
  }
  &.is-disabled {
    background-color: transparent;
    color: #dadce1;
    border-color: transparent;
  }
  &.is-loading .common-loading {
    background-color: #fff;
  }
  &.is-active {
    background-color: transparent;
    color: #4066d4;
    border-color: transparent;
  }
}
.common-button--large {
  padding: 0px 12px;
  font-size: 16px;
  line-height: 38px;
  height: 40px;
}
.common-button--small {
  padding: 0px 10px;
  font-size: 12px;
  line-height: 30px;
  height: 32px;
}
.common-button--mini {
  padding: 0px 8px;
  font-size: 12px;
  line-height: 22px;
  height: 24px;
}
.common-button--standardMedium {
  padding: 0px 16px;
  font-size: 12px;
  line-height: 23px;
  height: 32px;
}
.common-button--standardSmall {
  padding: 0px 12px;
  font-size: 12px;
  line-height: 22px;
  height: 26px;
}
</style>
